import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { base44 } from "@/api/base44Client";
import { AlertTriangle, Plus, CheckCircle, User } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { formatDistanceToNow } from 'date-fns';

export default function ActivityIssuesSection({ activity, officers, onReportIssue }) {
  const queryClient = useQueryClient();

  // Fetch Issues for this Activity
  const { data: issues = [] } = useQuery({
    queryKey: ['issues', activity?.id],
    queryFn: async () => {
      const allIssues = await base44.entities.Issue.list();
      return allIssues.filter(i => i.linked_activity_id === activity?.id);
    },
    enabled: !!activity?.id,
    refetchInterval: 5000
  });

  const resolveIssueMutation = useMutation({
    mutationFn: (issueId) => base44.entities.Issue.update(issueId, { status: 'resolved' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['issues'] });
    }
  });

  const openIssues = issues.filter(i => i.status !== 'resolved');
  const resolvedIssues = issues.filter(i => i.status === 'resolved');

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex flex-col">
          <h3 className="text-sm font-bold text-slate-100 uppercase tracking-widest flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-rose-400" />
            Issues & Problems
          </h3>
          <span className="text-[10px] text-slate-500 mt-1">
            {openIssues.length} Open | {resolvedIssues.length} Resolved
          </span>
        </div>
        <Button 
          size="sm" 
          className="bg-rose-600/20 hover:bg-rose-600/30 text-rose-400 border border-rose-600/50"
          onClick={onReportIssue}
        >
          <Plus className="w-3 h-3 mr-1" />
          Report Issue
        </Button>
      </div>

      <div className="space-y-3">
        {openIssues.length === 0 ? (
          <div className="text-center py-4 border border-dashed border-slate-800 rounded-lg text-xs text-slate-600">
            No open issues.
          </div>
        ) : (
          openIssues.map(issue => {
            const reporter = officers.find(o => o.user_email === issue.reported_by);
            return (
              <div key={issue.id} className="bg-slate-900/40 border border-rose-900/30 p-3 rounded-lg space-y-2 relative group hover:bg-slate-900/60 transition-colors">
                <div className="flex justify-between items-start gap-2">
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-rose-200">{issue.title}</h4>
                    <p className="text-xs text-slate-400 mt-1 line-clamp-2">{issue.description}</p>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 text-[10px] text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10"
                    onClick={() => resolveIssueMutation.mutate(issue.id)}
                  >
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Mark Resolved
                  </Button>
                </div>

                <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-800/50">
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1 text-[10px] text-slate-500">
                      <User className="w-3 h-3" />
                      <span>
                        {reporter ? `${reporter.callsign}, ${reporter.rank_title}` : issue.reported_by}
                      </span>
                      <span>•</span>
                      <span>{formatDistanceToNow(new Date(issue.reported_at), { addSuffix: true })}</span>
                    </div>
                  </div>
                  <Badge variant="outline" className="text-[9px] border-rose-500/30 text-rose-400 bg-rose-500/10 px-1.5 py-0">
                    {issue.status}
                  </Badge>
                </div>

                {/* Tags */}
                <div className="flex flex-wrap gap-1 mt-1">
                  {[
                    ...(issue.tagged_officers || []).map(t => ({ val: t, type: 'officer' })),
                    ...(issue.tagged_artifacts || []).map(t => ({ val: t, type: 'artifact' })),
                    ...(issue.tagged_policies || []).map(t => ({ val: t, type: 'policy' })),
                    ...(issue.tagged_rituals || []).map(t => ({ val: t, type: 'ritual' })),
                    ...(issue.tagged_capabilities || []).map(t => ({ val: t, type: 'capability' }))
                  ].map((tag, idx) => (
                    <Badge 
                        key={idx} 
                        variant="secondary" 
                        className={`text-[9px] px-1 h-4 ${
                            tag.type === 'officer' ? 'bg-indigo-500/10 text-indigo-300' :
                            tag.type === 'artifact' ? 'bg-cyan-500/10 text-cyan-300' :
                            tag.type === 'policy' ? 'bg-amber-500/10 text-amber-300' :
                            tag.type === 'ritual' ? 'bg-purple-500/10 text-purple-300' :
                            'bg-emerald-500/10 text-emerald-300'
                        }`}
                    >
                        {tag.type === 'officer' ? (officers.find(o => o.user_email === tag.val)?.callsign || tag.val) : tag.val}
                    </Badge>
                  ))}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Modal moved to parent */}
    </section>
  );
}